import { Box, InputAdornment, TextField, TextFieldProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Cancel as IconCancel } from '@mui/icons-material'
import IconSearch from 'assets/icons/icon-action-search.svg?react'
import { ICON_SIZE_MEDIUM } from '@carto/meridian-ds/theme'
import { FilledInputProps } from '@mui/material/FilledInput'

const IconSearchStyled = styled(IconSearch, {
  shouldForwardProp: (prop) => prop !== 'minimized' && prop !== 'disabled'
})<{ minimized?: boolean; disabled?: boolean }>(({ theme, minimized, disabled }) => ({
  cursor: !disabled && minimized ? 'pointer' : 'inherit',
  width: ICON_SIZE_MEDIUM,
  height: ICON_SIZE_MEDIUM,
  path: {
    fillOpacity: 1,
    fill: disabled ? theme.palette.text.disabled : theme.palette.text.secondary
  }
}))

const IconCancelStyled = styled(IconCancel)(({ theme }) => ({
  fill: theme.palette.text.hint,
  cursor: 'pointer'
}))

export const SearchTextFieldBase = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'minimized'
})<{ minimized: boolean }>(({ theme, disabled, minimized }) => ({
  '& .MuiInputBase-input': {
    cursor: disabled ? 'not-allowed' : 'text'
  },
  '& .MuiInputBase-root': {
    '&:before': {
      border: `none !important`,
      '&:hover': {
        border: `none !important`
      }
    },
    width: minimized ? theme.spacing(4) : '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    })
  },
  '& .MuiFilledInput-root': {
    transition: theme.transitions.create('background-color', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),
    backgroundColor: `${minimized ? 'inherit' : theme.palette.default.background} !important`,

    '&:hover': {
      backgroundColor: `${disabled ? 'inherit' : theme.palette.action.hover} !important`
    }
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: minimized ? theme.spacing(0.125) : undefined
  }
}))

export type SearchTextFieldProps = Omit<TextFieldProps, 'InputProps'> & {
  value?: string
  defaultValue?: string

  /**
   * minimized mode, which in which it only shows the search icon and user can deminimize it to render a full text field,
   *
   * @default false
   */
  minimized?: boolean

  /**
   * Called when `x` icon is clicked
   *
   * When not provided, control will emit reset as onChange with empty value
   */
  onResetSearch?: () => void
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  InputProps?: Partial<FilledInputProps> & { 'data-testid'?: string }
}

/**
 * SearchTextField - customized TextField with following search specific features:
 *  * hover/focused states according to design
 *  * search/reset icons according to design
 *  * reset icon resets value to empty
 *  * minimized mode, which in which it only shows the search icon and user can deminimize it to render a full text field, see ExpandedSearchTextField
 */
export default function SearchTextField({
  value,
  defaultValue,
  placeholder,
  onChange,
  onClick,
  onResetSearch,
  disabled,
  minimized = false,
  variant = 'filled',
  InputProps,
  startAdornment,
  endAdornment,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  focused: _focused,
  ...otherProps
}: SearchTextFieldProps) {
  const handleClearFilterText = () => {
    onResetSearch?.()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?.({ target: { value: '' } } as any)
  }

  return (
    <SearchTextFieldBase
      value={minimized ? '' : value}
      defaultValue={defaultValue}
      onChange={onChange}
      onClick={onClick}
      focused={false}
      disabled={disabled}
      minimized={minimized}
      placeholder={minimized ? '' : placeholder}
      variant={variant}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box display="inline-flex" ml={minimized ? -0.75 : 0}>
              {startAdornment || <IconSearchStyled disabled={disabled} minimized={minimized} />}
            </Box>
          </InputAdornment>
        ),
        endAdornment:
          !disabled && !minimized && onChange && (value ?? defaultValue) ? (
            <InputAdornment position="end">
              {endAdornment || <IconCancelStyled onClick={handleClearFilterText} />}
            </InputAdornment>
          ) : undefined,
        ...InputProps
      }}
      {...otherProps}
    />
  )
}
